import {
  CAMPAIGN_DOSSIER,
  ESCALATION_MAX,
  KIND_UNLOCK_CLEARS,
  defaultCampaign,
  totalClears,
  unlockedNodeKinds,
  type CampaignState,
} from "../meta/campaign";
import type { TheaterKey } from "../mission/variants";

// The campaign map (docs/design/GDD.md §The Campaign Layer): five theater
// dossiers on the world situation board. Playable theaters show REDFOR
// escalation and NG+ state and deploy a new Operation; the rest are INTEL
// PENDING. Pure renderer — campaign rules live in meta/campaign.ts.

const pips = (level: number): string =>
  Array.from({ length: ESCALATION_MAX }, (_, i) =>
    `<span class="theater-pip${i < level ? " theater-pip--hot" : ""}">▮</span>`).join("");

export class TheaterSelectScreen {
  private readonly el: HTMLElement;
  private campaign: CampaignState = defaultCampaign();

  constructor(private readonly callbacks: {
    onPick: (theater: TheaterKey) => void;
    onBack: () => void;
  }) {
    this.el = document.createElement("div");
    this.el.id = "theater-select";
    this.el.className = "screen-overlay screen-overlay--stacked";
    this.el.style.zIndex = "360";
    document.body.appendChild(this.el);
    this.el.addEventListener("click", this.handleClick);
    this.render();
  }

  setCampaign(campaign: CampaignState): void {
    this.campaign = campaign;
    this.render();
  }

  private render(): void {
    const cards = CAMPAIGN_DOSSIER.map((d) => {
      if (!d.theater) {
        return `
          <div class="screen-card screen-card--locked">
            <div class="screen-card__title">${d.codename}</div>
            <div class="screen-copy">${d.region}</div>
            <div class="screen-hint">${d.profile}</div>
            <div class="theater-locked">INTEL PENDING — NO TASKING AUTHORIZED</div>
          </div>`;
      }
      const t = this.campaign.theaters[d.theater];
      const cleared = t.clears > 0
        ? `<span class="theater-cleared">CLEARED ×${t.clears} — REDFOR REINFORCED</span>`
        : "";
      return `
        <div class="screen-card" data-theater="${d.theater}">
          <div class="screen-card__title">${d.codename}</div>
          <div class="screen-copy">${d.region}</div>
          <div class="screen-hint">${d.profile}</div>
          <div class="theater-activity">REDFOR ACTIVITY ${pips(t.escalation)} ${cleared}</div>
          <div class="screen-actions" style="justify-content:flex-start;margin-top:10px">
            <button class="screen-button screen-button--small" data-theater="${d.theater}">DEPLOY</button>
          </div>
        </div>`;
    }).join("");

    // Tasking authorization line: what the run map may roll, and what the
    // next theater clears buy.
    const clears = totalClears(this.campaign);
    const unlocked = unlockedNodeKinds(clears);
    const gate = (kind: "destroy" | "ambush" | "rescue" | "delay" | "sead" | "extraction", label: string) =>
      unlocked.has(kind)
        ? label
        : `<span class="theater-tasking-locked">${label} — ${KIND_UNLOCK_CLEARS[kind]} CLEAR${(KIND_UNLOCK_CLEARS[kind] ?? 0) > 1 ? "S" : ""}</span>`;
    const tasking = [
      "ESCORT", "DEFEND",
      gate("destroy", "FIRE MSN"),
      gate("ambush", "AMBUSH"),
      gate("rescue", "RESCUE"),
      gate("delay", "DELAY"),
      gate("sead", "SEAD"),
      gate("extraction", "EXFIL U/F"),
    ].join(" &nbsp;·&nbsp; ");

    this.el.innerHTML = `
      <div class="screen-kicker">TOP SECRET // NOFORN &nbsp;—&nbsp; WORLD SITUATION, 1989 &nbsp;—&nbsp; DAY ${this.campaign.day.toString().padStart(3, "0")}</div>
      <div class="screen-title screen-title--compact">CAMPAIGN MAP</div>
      <div class="screen-card-grid screen-card-grid--five">${cards}</div>
      <div class="theater-tasking">TASKING AUTHORIZED: &nbsp;${tasking}</div>
      <div class="screen-actions" style="margin-top:10px">
        <button id="theater-back" class="screen-button screen-button--muted screen-button--small">BACK</button>
      </div>
    `;
  }

  show(): void {
    this.el.classList.add("is-visible");
  }

  hide(): void {
    this.el.classList.remove("is-visible");
  }

  private handleClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest("#theater-back")) {
      this.hide();
      this.callbacks.onBack();
      return;
    }
    const key = target.closest<HTMLElement>("[data-theater]")?.dataset["theater"] as TheaterKey | undefined;
    if (key) {
      this.hide();
      this.callbacks.onPick(key);
    }
  };
}
